<script setup lang="ts">
import { computed, ref } from 'vue';
import type { DashboardCategoryType } from '@/types/dashboard/DashboardTypes';
import { BBtnIcon, BIcon } from '@/design-system';

type PropsType = {
    title: string;
    icon?: string;
    category?: DashboardCategoryType;
    categoryColor?: string;
    collapsible?: boolean;
    defaultExpanded?: boolean;
};

const props = withDefaults(defineProps<PropsType>(), {
    collapsible: false,
    defaultExpanded: true,
});

const categoryColors: Record<DashboardCategoryType, string> = {
    rfid: 'accent-mint',
    retail: 'accent-purple',
    logistics: 'accent-orange',
    manufacturing: 'accent-pink',
};

const isExpanded = ref(props.defaultExpanded);

const toggleExpanded = () => {
    if (props.collapsible) {
        isExpanded.value = !isExpanded.value;
    }
};

const resolvedColor = computed(() => {
    if (props.categoryColor) {
        return props.categoryColor;
    }
    if (props.category) {
        return categoryColors[props.category];
    }

    return 'primary';
});

const iconBackgroundStyle = computed(() => ({
    backgroundColor: `rgb(var(--v-theme-${resolvedColor.value}), 0.1)`,
    border: `1px solid rgb(var(--v-theme-${resolvedColor.value}), 0.3)`,
}));

const chevronRotation = computed(() => (isExpanded.value ? 'rotate(0deg)' : 'rotate(-90deg)'));
</script>

<template>
    <div class="b-dashboard-section dashboard-animate-section">
        <div class="b-dashboard-section__header">
            <div class="d-flex align-center ga-2">
                <div
                    v-if="icon"
                    class="b-dashboard-section__icon-container"
                    :style="iconBackgroundStyle"
                >
                    <BIcon
                        :icon="icon"
                        :color="resolvedColor"
                        size="default"
                    />
                </div>
                <h2 class="b-dashboard-section__title">
                    {{ title }}
                </h2>
            </div>

            <BBtnIcon
                v-if="collapsible"
                icon="mdi-chevron-down"
                size="small"
                variant="text"
                :style="{ transform: chevronRotation, transition: 'transform 0.3s ease' }"
                @click="toggleExpanded"
            />
        </div>

        <div
            v-show="isExpanded"
            class="b-dashboard-section__content"
        >
            <slot />
        </div>
    </div>
</template>

<style scoped lang="scss">
@use '@/design-system/styles/dashboard-animations';

.b-dashboard-section {
    margin-bottom: 32px;
}

.b-dashboard-section__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.b-dashboard-section__icon-container {
    width: 32px;
    height: 32px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.b-dashboard-section__title {
    font-size: 16px;
    font-weight: 600;
    color: rgb(var(--v-theme-grey-700));
    margin: 0;
}

.b-dashboard-section__content {
    margin-top: 16px;
}
</style>
